import is from '@sindresorhus/is';

import type {
    UrlTileCommentsGroup,
    UrlTileCommentsGroupInput,
    UrlTileDefinition,
    UrlTileDefinitionInput,
    UrlTilePayload,
    UrlTileWidthInput,
} from './types';

export const splitUrlTileValue = (value: string): readonly string[] => value.split('.');

const resolveWidth = <TPayload>(
    width: UrlTileWidthInput<TPayload> | undefined,
    payload: TPayload,
): string | undefined => (is.function(width) ? width(payload) : width);

export const createUrlTileDefinition = <TPayload extends UrlTilePayload, TProps>(
    input: UrlTileDefinitionInput<TPayload, TProps>,
): UrlTileDefinition => {
    const parsePayload = (payload: UrlTilePayload): TPayload | undefined => {
        const parsed = input.schema.safeParse(payload);

        return parsed.success ? parsed.data : undefined;
    };

    const toCommentsGroup = (commentsGroup: UrlTileCommentsGroupInput<TPayload>): UrlTileCommentsGroup => {
        const groupKey = (payload: UrlTilePayload): string | undefined => {
            const parsedPayload = parsePayload(payload);

            return is.undefined(parsedPayload) ? undefined : commentsGroup.groupKey(parsedPayload);
        };

        const toIsCommentsOpen =
            (isCommentsOpen: (payload: TPayload) => boolean) =>
            (payload: UrlTilePayload): boolean => {
                const parsedPayload = parsePayload(payload);

                return is.undefined(parsedPayload) ? false : isCommentsOpen(parsedPayload);
            };

        if (commentsGroup.role === 'child') {
            return {
                groupKey,
                ...(is.undefined(commentsGroup.isCommentsOpen)
                    ? {}
                    : { isCommentsOpen: toIsCommentsOpen(commentsGroup.isCommentsOpen) }),
                parentType: commentsGroup.parentType,
                role: 'child',
            };
        }

        return {
            groupKey,
            isCommentsOpen: toIsCommentsOpen(commentsGroup.isCommentsOpen),
            role: 'parent',
        };
    };

    return {
        ...(is.undefined(input.commentsGroup) ? {} : { commentsGroup: toCommentsGroup(input.commentsGroup) }),
        holdAutoScroll: input.holdAutoScroll,
        parseValue: (value) => {
            const parsed = input.schema.safeParse(input.codec.parseTokens(splitUrlTileValue(value)));

            return parsed.success ? parsed.data : undefined;
        },
        renderPayload: (payload, context) => {
            const parsedPayload = parsePayload(payload);

            if (is.undefined(parsedPayload)) {
                return undefined;
            }

            return input.render(input.toProps(parsedPayload, context));
        },
        serializePayload: (payload) => {
            const parsedPayload = parsePayload(payload);

            return is.undefined(parsedPayload) ? undefined : input.codec.serialize(parsedPayload);
        },
        slotKeyForPayload: (payload) => {
            const parsedPayload = parsePayload(payload);

            return is.undefined(parsedPayload) ? undefined : input.slotKey(parsedPayload);
        },
        tileKeyForPayload: (payload) => {
            const parsedPayload = parsePayload(payload);

            return is.undefined(parsedPayload) ? undefined : input.tileKey(parsedPayload);
        },
        type: input.codec.type,
        widthsForPayload: (payload) => {
            const parsedPayload = parsePayload(payload);

            if (is.undefined(parsedPayload)) {
                return {};
            }

            return {
                maxWidth: resolveWidth(input.maxWidth, parsedPayload),
                minWidth: resolveWidth(input.minWidth, parsedPayload),
                width: resolveWidth(input.width, parsedPayload),
            };
        },
        zIndex: input.zIndex,
    };
};
